/**
 * ShopPage — Phase 4e.E (Feb 2026)
 *
 * Öffentlicher Shop unter `/shop/:dealerSlug`.
 * Listet alle `visible_b2c=true` Artikel des Händlers mit Suche + Filter.
 * Anfragen werden via Modal als ShopEnquiry an den Händler weitergeleitet.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Search, MapPin, Phone, Mail, Loader2, ShoppingBag, ShoppingCart, MessageSquare, X } from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import useShopSeo from "../lib/useShopSeo";
import { useShopCart } from "../lib/shopCart";
import { CartBadgeButton, ShopCartDrawer } from "../components/ShopCartDrawer";

const API = process.env.REACT_APP_BACKEND_URL;

function ShopPhoto({ path }) {
    const [err, setErr] = useState(false);
    const base = `${API}/api/shop/photo/${path}`;
    const src = `${base}?w=640&fmt=webp`;
    // Phase 4f.D — srcset für Retina + Mobile; WebP-First mit JPEG-Fallback
    const srcSetWebp = path ? [
        `${base}?w=320&fmt=webp 320w`,
        `${base}?w=640&fmt=webp 640w`,
        `${base}?w=1024&fmt=webp 1024w`,
    ].join(", ") : null;
    if (err || !path) {
        return <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-300"><ShoppingBag className="w-10 h-10" /></div>;
    }
    return (
        <picture>
            <source type="image/webp" srcSet={srcSetWebp} sizes="(max-width: 768px) 50vw, 25vw" />
            <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" onError={() => setErr(true)} />
        </picture>
    );
}

export default function ShopPage() {
    const { dealerSlug } = useParams();
    const [shop, setShop] = useState(null);
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("");
    const [enquiryArticle, setEnquiryArticle] = useState(null);
    const [cartOpen, setCartOpen] = useState(false);
    const { addItem } = useShopCart(dealerSlug);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (search) params.search = search;
            if (category) params.category = category;
            const r = await axios.get(`${API}/api/shop/${dealerSlug}`, { params });
            setShop(r.data.shop);
            setArticles(r.data.articles);
        } catch (e) {
            toast.error(e.response?.status === 404 ? "Shop nicht gefunden" : "Fehler beim Laden");
            setShop(null);
        }
        setLoading(false);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [dealerSlug]);

    // Phase 4e.F — SEO Schema.org-Markup + OG-Tags
    useShopSeo({ shop, articles, articleDetail: null });

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-[#FFCC00]" /></div>;
    }
    if (!shop) {
        return (
            <div className="min-h-screen flex items-center justify-center flex-col gap-3">
                <ShoppingBag className="w-12 h-12 text-gray-300" />
                <div className="text-lg font-bold">Shop nicht gefunden</div>
                <Link to="/" className="text-blue-600 underline">Zur Startseite</Link>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50" data-testid="shop-page">
            {/* Header */}
            <header className="bg-white border-b border-gray-200">
                <div className="max-w-6xl mx-auto px-4 py-6 flex items-center gap-4">
                    {shop.company_logo_url ? (
                        <img src={shop.company_logo_url} alt="" className="w-16 h-16 rounded-sm border border-gray-200 object-contain" />
                    ) : (
                        <div className="w-16 h-16 rounded-sm border-2 border-[#111827] bg-[#FFCC00] flex items-center justify-center font-display font-black text-2xl">
                            {(shop.company_name || "?").charAt(0)}
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <h1 className="font-display text-2xl font-black tracking-tight" data-testid="shop-company-name">{shop.company_name || dealerSlug}</h1>
                        <div className="text-xs text-gray-600 mt-1 flex items-center gap-3 flex-wrap">
                            {shop.city && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{shop.city}</span>}
                            {shop.phone_office && <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{shop.phone_office}</span>}
                            {shop.contact_email && <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{shop.contact_email}</span>}
                            {shop.verified && <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider">Verifiziert</span>}
                        </div>
                    </div>
                    {shop.ansprechpartner && (
                        <div className="hidden sm:flex items-center gap-2 pl-4 mr-2 border-l border-gray-200" data-testid="shop-ansprechpartner-block">
                            {shop.ansprechpartner.photo_url ? (
                                <img src={shop.ansprechpartner.photo_url} alt={shop.ansprechpartner.name} className="w-11 h-11 rounded-full object-cover border border-gray-200" data-testid="shop-ansprechpartner-avatar" />
                            ) : (
                                <span className="w-11 h-11 rounded-full bg-[#111827] text-[#FFCC00] inline-flex items-center justify-center font-display font-black text-xs" data-testid="shop-ansprechpartner-avatar">
                                    {(shop.ansprechpartner.name || "?").split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                                </span>
                            )}
                            <div className="leading-tight">
                                <div className="text-[9px] font-bold uppercase tracking-wider text-gray-400">Ansprechpartner</div>
                                <div className="text-xs font-bold text-[#111827]" data-testid="shop-ansprechpartner-name">{shop.ansprechpartner.name}</div>
                            </div>
                        </div>
                    )}
                    <CartBadgeButton slug={dealerSlug} onClick={() => setCartOpen(true)} />
                </div>
            </header>

            {/* Toolbar */}
            <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-2 flex-wrap" data-testid="shop-toolbar">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-emerald-600" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                        placeholder="Suche…"
                        className="pl-8 rounded-sm h-9 border-2 border-emerald-500 focus-visible:ring-emerald-400 placeholder:text-emerald-700/70"
                        data-testid="shop-search-input"
                    />
                </div>
                <select
                    value={category}
                    onChange={(e) => { setCategory(e.target.value); setTimeout(load, 0); }}
                    className="rounded-sm border border-gray-300 h-9 px-2 text-sm"
                    data-testid="shop-category-select"
                >
                    <option value="">Alle Kategorien</option>
                    <option value="part">Teile</option>
                    <option value="tire">Reifen</option>
                    <option value="accessory">Zubehör</option>
                    <option value="fluid">Flüssigkeiten</option>
                    <option value="other">Sonstiges</option>
                </select>
                <Button onClick={load} className="h-9 rounded-sm bg-[#111827] hover:bg-black text-white" data-testid="shop-search-btn">Suchen</Button>
            </div>

            {/* Articles Grid */}
            <main className="max-w-6xl mx-auto px-4 pb-8">
                {articles.length === 0 ? (
                    <div className="text-center py-12 text-gray-500" data-testid="shop-empty">
                        <ShoppingBag className="w-12 h-12 mx-auto text-gray-300" />
                        <div className="mt-3">Keine Artikel verfügbar.</div>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="shop-articles-grid">
                        {articles.map((a) => (
                            <div key={a.id} className="bg-white rounded-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow" data-testid={`shop-article-${a.id}`}>
                                <div className="aspect-square bg-gray-50 relative">
                                    <ShopPhoto path={(a.photo_paths || [])[0]} />
                                    {a.ai_generated_images && (
                                        <span className="absolute bottom-1.5 left-1.5 bg-black/60 text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm" data-testid={`shop-ai-image-badge-${a.id}`}>
                                            KI-generiertes Bild
                                        </span>
                                    )}
                                </div>
                                <div className="p-3 space-y-1">
                                    <div className="font-bold text-sm line-clamp-2" data-testid={`shop-article-name-${a.id}`}>{a.name}</div>
                                    <div className="text-[10px] text-gray-500 font-mono-num">SKU: {a.sku}</div>
                                    <div className="flex items-baseline justify-between mt-2">
                                        <div className="font-mono-num font-black text-lg" data-testid={`shop-article-price-${a.id}`}>
                                            {a.price_gross > 0 ? `${a.price_gross.toFixed(2)} €` : <span className="text-gray-400 text-sm">Auf Anfrage</span>}
                                        </div>
                                        <div className="text-[10px] text-gray-500">{a.stock_qty > 0 ? "Lieferbar" : "Anfragen"}</div>
                                    </div>
                                    <Button
                                        type="button"
                                        onClick={() => {
                                            if (!a.price_gross || a.price_gross <= 0) {
                                                toast.error("Artikel ohne Preis — bitte anfragen");
                                                return;
                                            }
                                            addItem({ id: shop.dealer_id, dealer_id: shop.dealer_id, company_name: shop.company_name }, a, 1);
                                            toast.success(`„${a.name}" in den Warenkorb gelegt`);
                                            setCartOpen(true);
                                        }}
                                        disabled={!a.price_gross || a.price_gross <= 0}
                                        className="w-full mt-2 h-8 rounded-sm bg-[#111827] hover:bg-black disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold text-xs"
                                        data-testid={`shop-add-to-cart-${a.id}`}
                                    >
                                        <ShoppingCart className="w-3.5 h-3.5 mr-1" />
                                        In den Warenkorb
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setEnquiryArticle(a)}
                                        className="w-full mt-1.5 h-7 rounded-sm border-gray-300 text-gray-700 hover:bg-gray-50 font-medium text-[11px]"
                                        data-testid={`shop-enquiry-btn-${a.id}`}
                                    >
                                        <MessageSquare className="w-3 h-3 mr-1" />
                                        Anfragen
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>

            {enquiryArticle && (
                <EnquiryModal
                    article={enquiryArticle}
                    dealerSlug={dealerSlug}
                    onClose={() => setEnquiryArticle(null)}
                />
            )}
            <ShopCartDrawer slug={dealerSlug} open={cartOpen} onClose={() => setCartOpen(false)} dealerInfo={shop} />
        </div>
    );
}

function EnquiryModal({ article, dealerSlug, onClose }) {
    const [form, setForm] = useState({ visitor_name: "", visitor_email: "", visitor_phone: "", message: "" });
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!form.visitor_name.trim() || !form.visitor_email.trim() || !form.message.trim()) {
            toast.error("Bitte Name, E-Mail und Nachricht ausfüllen");
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/api/shop/enquiry`, {
                dealer_slug: dealerSlug,
                article_id: article.id,
                ...form,
            });
            toast.success("Anfrage gesendet — der Händler meldet sich in Kürze!");
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Anfrage fehlgeschlagen");
        }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="shop-enquiry-modal">
            <div className="bg-white rounded-sm w-full max-w-md p-5 space-y-3">
                <div className="flex items-center justify-between">
                    <h2 className="font-display font-bold text-lg">Anfrage: {article.name}</h2>
                    <button onClick={onClose} data-testid="shop-enquiry-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Name *</Label>
                        <Input value={form.visitor_name} onChange={(e) => setForm({ ...form, visitor_name: e.target.value })} className="rounded-sm mt-1" data-testid="enquiry-name" />
                    </div>
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">E-Mail *</Label>
                        <Input type="email" value={form.visitor_email} onChange={(e) => setForm({ ...form, visitor_email: e.target.value })} className="rounded-sm mt-1" data-testid="enquiry-email" />
                    </div>
                    <div className="sm:col-span-2">
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Telefon (optional)</Label>
                        <Input value={form.visitor_phone} onChange={(e) => setForm({ ...form, visitor_phone: e.target.value })} className="rounded-sm mt-1" data-testid="enquiry-phone" />
                    </div>
                    <div className="sm:col-span-2">
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Nachricht *</Label>
                        <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-sm mt-1" rows={4} placeholder={`Ich interessiere mich für "${article.name}" (SKU ${article.sku})…`} data-testid="enquiry-message" />
                    </div>
                </div>
                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-sm" data-testid="enquiry-cancel">Abbrechen</Button>
                    <Button onClick={submit} disabled={busy} className="rounded-sm bg-[#111827] hover:bg-black text-white" data-testid="enquiry-submit">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MessageSquare className="w-4 h-4 mr-2" />}
                        Anfrage senden
                    </Button>
                </div>
            </div>
        </div>
    );
}
